import React, { FC, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import { useAtom } from 'jotai'
import { Button } from '../../ui/Button/Button'
import {
    ContactLedger,
    Edit,
    PersonalInfo,
    LockFill,
    CheckFill,
    SuitcaseFill,
} from '../../../assets/icons'
import { Circle } from '../../ui/Card'
import { CircleIcon } from '../../ui/Circle/Circle'
import { Typography } from '../../ui/Typography/Typography'
import { Input } from '../../ui/FormElememts/stories/Input.stories'
import { colors } from '../../../theme/palette'
import { formatDate } from '../../../utils/format'
import { FormInput, FormInputNumber } from '../FormElements/FormInput'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { FormHeader } from '../../ui/FormElememts/FormHeader'
import { baseButton } from '../../../theme/reuseables'
import { Overlay } from '../../ui/Sidebar'
import { Dialog } from '../../ui/Dialog/Dialog'
import { DrawerHeaderMemberInfo } from '../FormElements/DrawerHeader'
import { Profile } from '../../../models/Profile'
import {
    getProfileAtom,
    updateProfile,
    profileAtom,
} from '../../../api/profile/profile'
import { FormMessage } from '../../ui/FormElememts/FormMessage'
import { L, L_HTML } from '../../providers/Lokalise'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'

interface EditProps {
    profile: Profile
}

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: '100%',
        paddingBottom: '80px',
    },
    accordionWrapper: {
        marginBottom: '20px',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

export type RegistrationFormFields = {
    KontaktTR: {
        Mobil: string
        Email: string
    }
    TotalAntalKollegaerBrugerDaekker: number
}

export interface IUpdateMyProfileData {
    KontaktTR?: {
        Mobil?: string
        Email?: string
    }
    TotalAntalKollegaerBrugerDaekker?: number
}

export interface IUpdateMyProfileUserCovers {
    TotalAntalKollegaerBrugerDaekkerTR?: number
    TotalAntalKollegaerBrugerDaekkerFTR?: number
}

export const EditMyProfile: FC<EditProps> = ({ profile }) => {
    const { register, handleSubmit, formState, reset } =
        useForm<RegistrationFormFields>()
    const classes = useStyles()
    const [isOpen, setIsOpen] = useState(false)
    const [showDialog, setShowDialog] = useState(false)
    const [, update] = useAtom(getProfileAtom)
    const { isDirty, dirtyFields, errors } = formState
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)

    const resetForm = (): void => {
        reset()
    }

    const onSubmit = async (data: RegistrationFormFields): Promise<void> => {
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return
        }

        if (Object.keys(dirtyFields).length) {
            const fieldsToEdit: IUpdateMyProfileData = {}

            if (dirtyFields.KontaktTR) {
                fieldsToEdit.KontaktTR = {}
                if (
                    dirtyFields.KontaktTR.Mobil &&
                    data.KontaktTR.Mobil !== ''
                ) {
                    fieldsToEdit.KontaktTR.Mobil = data.KontaktTR.Mobil
                }
                if (
                    dirtyFields.KontaktTR.Email &&
                    data.KontaktTR.Email !== ''
                ) {
                    fieldsToEdit.KontaktTR.Email = data.KontaktTR.Email
                }
            }

            try {
                const response = updateProfile(fieldsToEdit)
                if (response) {
                    update({ type: 'refetch' })
                    setIsOpen(false)
                    setShowDialog(true)
                    resetForm()
                }
            } catch (err) {
                console.log('fetch failed', err)
            }
        }
    }

    return (
        <>
            <button
                type="button"
                className={classes.button}
                onClick={() => setIsOpen(true)}
            >
                <Circle marginRight=".5rem">
                    <CircleIcon>
                        <Edit />
                    </CircleIcon>
                </Circle>
                <Typography tag="h3" size="M" spacing=".2rem">
                    {L('TRPROFIL_DETALJE_REDIGERMEDLEM_KNAP')}
                </Typography>
            </button>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    reset()
                    setIsOpen(false)
                }}
            >
                <DrawerHeaderMemberInfo profile={profile} />
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={classes.contentWrapper}
                >
                    <Typography
                        tag="h2"
                        size="XL"
                        align="center"
                        weight="bold"
                        style={{ margin: '20px 0 10px' }}
                    >
                        {L('TR-PROFIL_REDIGER_DATA_OVERSKRIFT')}
                    </Typography>

                    <FormHeader>
                        <Circle marginRight=".5rem">
                            <CircleIcon iconWidth="16px">
                                <ContactLedger />
                            </CircleIcon>
                        </Circle>
                        <Typography tag="h3" size="M" spacing=".2rem">
                            Kontakt
                        </Typography>
                    </FormHeader>
                    <FormControl>
                        <FormInputNumber
                            label="Tillidsvalgt mobil"
                            name="KontaktTR.Mobil"
                            required={false}
                            register={register}
                            defaultValue={profile.privateContact?.mobile}
                            hasError={!!errors?.KontaktTR?.Mobil.message}
                            rules={{
                                validate: (value) =>
                                    value.length > 7 ||
                                    !value ||
                                    L('GENERISK_FORM_MOBIL_VALIDERINGS_TEKST'),
                            }}
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_MOBIL_VALIDERINGS_TEKST')}
                            error={errors?.KontaktTR?.Mobil.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormInput
                            label="Tillidsvalgt email"
                            name="KontaktTR.Email"
                            required={false}
                            register={register}
                            type="email"
                            defaultValue={profile.privateContact?.email || ''}
                        />
                    </FormControl>
                    <div className={classes.bottom}>
                        <Button
                            onClick={() => {
                                setIsOpen(false)
                                reset()
                            }}
                            type="secondary"
                            size="large"
                        >
                            Luk
                        </Button>
                        <Button
                            buttonType="submit"
                            type="primary"
                            size="large"
                            disabled={!isDirty}
                        >
                            {L('TR-PROFIL_REDIGER_DATA_INDSEND_KNAP')}
                        </Button>
                    </div>

                    <div />
                </form>
            </Overlay>
            {showDialog ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<LockFill width="46px" />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        {L_HTML('EGEN-PROFIL-REDIGER_POPUP_OVERSKRIFT')}
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {L_HTML('EGEN-PROFIL-REDIGER_POPUP_TEXT')}
                    </Typography>
                    <Button size="xlarge" onClick={() => setShowDialog(false)}>
                        {L_HTML('EGEN-PROFIL-REDIGER_POPUP_KNAP')}
                    </Button>
                </Dialog>
            ) : null}
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </>
    )
}
